//Normalização das taxas de fundo exibidas na aba de Custos.
//
//O objetivo aqui é separar três situações que o código antigo colapsava no mesmo "-":
//  - a taxa foi informada (inclusive quando o valor é zero);
//  - a taxa se aplica ao fundo, mas não foi divulgada pela fonte;
//  - a taxa não se aplica àquele produto.
//
//Regra que não pode ser violada: "não aplicável" só é afirmado quando existe evidência
//concreta no sistema (cadastro do fundo ou marcador textual da própria fonte). Ausência
//de valor NUNCA é tratada como "não aplicável", e zero NUNCA é tratado como ausência.

export const RATE_STATUS = {
    INFORMED: 'informed',
    NOT_INFORMED: 'not_informed',
    NOT_APPLICABLE: 'not_applicable',
};

export const RATE_REASON = {
    POUPANCA: 'poupanca',
    SOURCE_SAYS_NONE: 'source_says_none',
    CHARGES_UNDISCLOSED: 'charges_undisclosed',
    API_FAILED: 'api_failed',
    NO_INFOS_API: 'no_infos_api',
    FUND_NOT_RETURNED: 'fund_not_returned',
    UNPARSABLE: 'unparsable',
    EMPTY_CELL: 'empty_cell',
    MANUAL: 'manual',
};

//Rótulos da legenda
export const RATE_STATUS_LABEL = {
    [RATE_STATUS.INFORMED]: 'Informado pelo fundo',
    [RATE_STATUS.NOT_INFORMED]: 'Não informado',
    [RATE_STATUS.NOT_APPLICABLE]: 'Não aplicável',
};

//Rótulo da legenda para taxas ajustadas manualmente (client_fund_rates). Não é um status:
//a taxa ajustada é INFORMED, mas é marcada com ícone próprio.
export const MANUAL_RATE_LABEL = 'Ajustado manualmente';

//Texto detalhado por motivo, usado em title/aria-label. Permite explicar o "porquê"
//sem multiplicar estados visuais.
const RATE_REASON_LABEL = {
    [RATE_REASON.POUPANCA]: 'Não aplicável: poupança não possui taxas de fundo',
    [RATE_REASON.SOURCE_SAYS_NONE]: 'Não aplicável: o fundo não cobra taxa de performance',
    [RATE_REASON.CHARGES_UNDISCLOSED]: 'Não informado: o fundo cobra taxa de performance, mas o percentual não foi divulgado',
    [RATE_REASON.API_FAILED]: 'Não informado: não foi possível consultar a fonte de dados',
    [RATE_REASON.NO_INFOS_API]: 'Não informado: fundo cadastrado como sem informações na API',
    [RATE_REASON.FUND_NOT_RETURNED]: 'Não informado: fundo não retornado pela fonte de dados',
    [RATE_REASON.UNPARSABLE]: 'Não informado: a fonte devolveu um valor não numérico',
    [RATE_REASON.EMPTY_CELL]: 'Não informado pelo fundo',
    [RATE_REASON.MANUAL]: MANUAL_RATE_LABEL,
};

//Símbolos usados no PDF, onde não há ícone
export const RATE_STATUS_SYMBOL = {
    [RATE_STATUS.NOT_INFORMED]: 'n.i.',
    [RATE_STATUS.NOT_APPLICABLE]: 'n.a.',
};

//Marcadores textuais com que a fonte indica que o fundo NÃO cobra a taxa.
//Comparados já sem acento e em minúsculo. Evidência do uso de "N": o campo equivalente
//na origem ANBIMA chama-se cobra_taxa_performance (FundController.js) e os relatórios de
//APR já traduzem "N" para "Não possui" (ReportController.js).
//"n/d" e afins ficam de fora de propósito: significam "não disponível", que é não informado.
const NO_CHARGE_MARKERS = [
    'n',
    'nao',
    'nao possui',
    'nao cobra',
    'nao ha',
    'sem taxa',
    'nao aplicavel',
];

//Marcadores textuais com que a fonte indica que o fundo cobra, sem dizer quanto.
const CHARGE_MARKERS = ['s', 'sim', 'possui', 'cobra'];

//Faixa de sinais diacríticos combinantes, para remover acentos após normalize('NFD').
//Mesma técnica usada em comdinheiro-api.js, aqui montada a partir de string para manter
//o arquivo em ASCII puro.
const ACCENTS_REGEX = new RegExp('[\\u0300-\\u036f]', 'g');

function normalizeMarker(raw) {
    if (raw == null) return '';

    return String(raw)
        .trim()
        .normalize('NFD')
        .replace(ACCENTS_REGEX, '')
        .replace(/\s+/g, ' ')
        .toLowerCase();
}

export function isNoChargeMarker(raw) {
    const marker = normalizeMarker(raw);
    return marker !== '' && NO_CHARGE_MARKERS.includes(marker);
}

export function isChargeMarker(raw) {
    const marker = normalizeMarker(raw);
    return marker !== '' && CHARGE_MARKERS.includes(marker);
}

//Separa o valor numérico do valor bruto devolvido pela fonte.
//`parsedValue` vem de dados_cadastrais; `rawValue` de raw_taxes. Os dois são preservados:
//o número decide se a taxa foi informada, o bruto decide aplicabilidade.
export function normalizeRate(rawValue, parsedValue) {
    const raw = rawValue == null || rawValue === '' ? null : String(rawValue).trim();
    const value = Number.isFinite(parsedValue) ? parsedValue : null;

    return { value, raw };
}

//Única função que decide o estado semântico de uma célula de taxa.
//A ordem importa: aplicabilidade primeiro, valor depois, motivo da ausência por último.
export function resolveRateStatus({ value, raw, kind, source }) {
    const src = source || {};

    //NÃO APLICÁVEL — somente com evidência concreta.
    //Poupança não é fundo: não há administrador, gestor, distribuidor nem custodiante
    //remunerado por taxa.
    if (src.isPoupanca) {
        return { status: RATE_STATUS.NOT_APPLICABLE, reason: RATE_REASON.POUPANCA };
    }

    //A própria fonte declara que o fundo não cobra performance.
    if (kind === 'performance' && isNoChargeMarker(raw)) {
        return { status: RATE_STATUS.NOT_APPLICABLE, reason: RATE_REASON.SOURCE_SAYS_NONE };
    }

    //INFORMADO — inclui explicitamente o zero, que é valor válido.
    if (Number.isFinite(value)) {
        return { status: RATE_STATUS.INFORMED, reason: null };
    }

    //NÃO INFORMADO — o motivo mais específico disponível.
    //A fonte confirmou que cobra, mas não divulgou o percentual: é aplicável e não informado.
    if (kind === 'performance' && isChargeMarker(raw)) {
        return { status: RATE_STATUS.NOT_INFORMED, reason: RATE_REASON.CHARGES_UNDISCLOSED };
    }

    if (src.apiFailed) {
        return { status: RATE_STATUS.NOT_INFORMED, reason: RATE_REASON.API_FAILED };
    }

    if (src.noInfosApi) {
        return { status: RATE_STATUS.NOT_INFORMED, reason: RATE_REASON.NO_INFOS_API };
    }

    if (!src.found) {
        return { status: RATE_STATUS.NOT_INFORMED, reason: RATE_REASON.FUND_NOT_RETURNED };
    }

    if (raw != null) {
        return { status: RATE_STATUS.NOT_INFORMED, reason: RATE_REASON.UNPARSABLE };
    }

    return { status: RATE_STATUS.NOT_INFORMED, reason: RATE_REASON.EMPTY_CELL };
}

export function getRateTitle(status, reason) {
    return RATE_REASON_LABEL[reason] || RATE_STATUS_LABEL[status] || '';
}

//Formata a taxa para exibição. Zero devolve "0,00%" — nunca é escondido.
//Ausência devolve string vazia: quem comunica a ausência é o ícone de status.
export function formatRate(value) {
    if (!Number.isFinite(value)) return '';

    return value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }) + '%';
}

//Versão para o PDF, onde não há ícone: valor quando informado, sigla quando não.
export function formatRateToReport(rate) {
    if (!rate) return RATE_STATUS_SYMBOL[RATE_STATUS.NOT_INFORMED];

    if (rate.status === RATE_STATUS.INFORMED) {
        return formatRate(rate.value);
    }

    return RATE_STATUS_SYMBOL[rate.status] || RATE_STATUS_SYMBOL[RATE_STATUS.NOT_INFORMED];
}

//Regra da performance para o PDF: benchmark e, quando houver, as condições.
export function formatPerformanceRuleToReport(rule) {
    if (!rule || rule.status !== RATE_STATUS.INFORMED) {
        return (rule && RATE_STATUS_SYMBOL[rule.status]) || RATE_STATUS_SYMBOL[RATE_STATUS.NOT_INFORMED];
    }

    return rule.conditions ? `${rule.benchmark} | ${rule.conditions}` : rule.benchmark;
}

//Precedência do benchmark, espelhando o cruzamento que já existe em
//ComdinheiroController.js: o cadastro UNO (local_benchmarks) vence, porque é o benchmark
//que o consultor escolheu e o que o resto do sistema usa nas comparações de retorno.
//A comdinheiro entra apenas como fallback.
export function resolveFundBenchmark(asset, dadosCadastrais) {
    const fromUno = asset && (asset.benchmark_local || asset.benchmark);
    if (fromUno) {
        return { benchmark: fromUno, benchmarkSource: 'uno' };
    }

    const fromComdinheiro = dadosCadastrais && dadosCadastrais.benchmark;
    if (fromComdinheiro) {
        return { benchmark: fromComdinheiro, benchmarkSource: 'comdinheiro' };
    }

    return { benchmark: null, benchmarkSource: null };
}

//Cada coluna de taxa: chave de apresentação, campo na resposta da fonte e "tipo",
//usado para decidir aplicabilidade. O campo também é o nome da coluna em client_fund_rates.
export const RATE_FIELDS = [
    { key: 'global', field: 'max_administration_tax', kind: 'global', label: 'Tx. Global a.a. (%)' },
    { key: 'administration', field: 'administration_tax', kind: 'administration', label: 'Tx. Adm a.a. (%)' },
    { key: 'management', field: 'management_tax', kind: 'management', label: 'Tx. Gestão a.a. (%)' },
    { key: 'distribution', field: 'distribution_tax', kind: 'distribution', label: 'Tx. Máx. Distribuição a.a. (%)' },
    { key: 'custody', field: 'custody_tax', kind: 'custody', label: 'Tx. Custódia a.a. (%)' },
    { key: 'performance', field: 'performance_tax', kind: 'performance', label: 'Tx. Performance (%)' },
];

//Valor de um ajuste manual vindo do banco. NUMERIC chega do pg como string; null/'' = sem ajuste.
export function parseManualRate(value) {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
}

//Mantém o contrato antigo de cada taxa (número ou "-"), consumido pelo PDF.
function toLegacyValue(rate) {
    return rate.value == null ? '-' : rate.value;
}

function resolvePerformanceRule(rates, benchmarkInfo, source) {
    //Sem taxa de performance não há remuneração variável a condicionar.
    if (rates.performance.status === RATE_STATUS.NOT_APPLICABLE) {
        return {
            ...benchmarkInfo,
            conditions: null,
            status: RATE_STATUS.NOT_APPLICABLE,
            reason: rates.performance.reason,
        };
    }

    if (benchmarkInfo.benchmark) {
        return {
            ...benchmarkInfo,
            //As condições da regra (linha d'água, periodicidade, "acima do benchmark") não
            //possuem campo em nenhuma fonte do sistema hoje. Fica null de propósito — o
            //texto não é inventado.
            conditions: null,
            status: RATE_STATUS.INFORMED,
            reason: null,
        };
    }

    return {
        ...benchmarkInfo,
        conditions: null,
        status: RATE_STATUS.NOT_INFORMED,
        reason: source.found ? RATE_REASON.EMPTY_CELL : RATE_REASON.FUND_NOT_RETURNED,
    };
}

//Monta a linha da tabela de fundos: bloco novo de apresentação + as chaves legadas
//que o relatório em PDF consome.
//`manualRates` é a linha de client_fund_rates do fundo para o cliente, se houver. Taxa ajustada
//manualmente sempre prevalece sobre a fonte, inclusive sobre "não aplicável"; as não ajustadas
//seguem a resolução normal.
export function buildFundRateRow(asset, fundInfo, meta = {}, manualRates = null) {
    const dc = (fundInfo && fundInfo.dados_cadastrais) || {};
    const rawTaxes = (fundInfo && fundInfo.raw_taxes) || {};

    const source = {
        found: !!meta.found,
        apiFailed: !!meta.apiFailed,
        isPoupanca: !!(asset && asset.is_poupanca),
        noInfosApi: !!(asset && asset.no_infos_api),
    };

    const rates = {};
    RATE_FIELDS.forEach(({ key, field, kind }) => {
        const { value, raw } = normalizeRate(rawTaxes[field], dc[field]);
        const { status, reason } = resolveRateStatus({ value, raw, kind, source });

        const manualValue = manualRates ? parseManualRate(manualRates[field]) : null;
        if (manualValue !== null) {
            rates[key] = {
                value: manualValue,
                raw: null,
                status: RATE_STATUS.INFORMED,
                reason: RATE_REASON.MANUAL,
                manual: true,
                //O que a fonte diria sem o ajuste, para o tooltip e o painel de edição
                apiRate: { value, raw, status, reason },
            };
            return;
        }

        rates[key] = { value, raw, status, reason };
    });

    const performanceRule = resolvePerformanceRule(
        rates,
        resolveFundBenchmark(asset, dc),
        source,
    );

    return {
        //Contrato preservado: mesmas chaves e mesmos tipos de antes da reformulação
        fund_id: (asset && asset.fund_id) || null,
        fund_name: (asset && (asset.fund_name || asset.label)) || '-',
        cnpj: (asset && asset.cnpj) || '-',
        administration_tax: toLegacyValue(rates.administration),
        max_administration_tax: toLegacyValue(rates.global),
        performance_tax: toLegacyValue(rates.performance),
        custody_tax: toLegacyValue(rates.custody),
        management_tax: toLegacyValue(rates.management),
        distribution_tax: toLegacyValue(rates.distribution),

        //Bloco de apresentação
        rates,
        performanceRule,
        source,
        manualRates: manualRates || null,
    };
}
